/**
 * Перенос `CustomValueNotifier<T>` из `flutter_toolkit`.
 *
 * На нём построены `ChatController` и `ChatDurationController`. Это не
 * виджет и не провайдер, а обычный наблюдаемый контейнер состояния, поэтому
 * переносится без изменений; в React он подключается через
 * `useSyncExternalStore` (см. `useValueNotifier`).
 *
 * Жизненный цикл тот же, что в Dart:
 *   buildState()  → начальное значение
 *   initState()   → подписки и таймеры, уже с готовым `value`
 *   dispose()     → освобождение
 *
 * Наследник обязан вызвать `initialize()` в конце своего конструктора:
 * в Dart это делает базовый класс, но в TypeScript поля наследника ещё не
 * присвоены на момент работы конструктора базового класса.
 */
export abstract class CustomValueNotifier<T> {
  #value: T | undefined;
  #disposed = false;
  #initialized = false;
  readonly #listeners = new Set<() => void>();

  /** Начальное значение. Вызывается один раз в `initialize()`. */
  protected abstract buildState(): T;

  /** Подписки и таймеры. Вызывается после `buildState()`. */
  protected initState(): void {}

  /** Вызывается после каждой смены значения — сравнение по ссылке. */
  protected onStateChanged(_previous: T, _current: T): void {}

  /** Завершает построение. Вызывать в конце конструктора наследника. */
  protected initialize(): void {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#value = this.buildState();
    this.initState();
  }

  get value(): T {
    if (!this.#initialized) {
      throw new Error(
        `${this.constructor.name}: initialize() не вызван в конструкторе`,
      );
    }
    return this.#value as T;
  }

  get isDisposed(): boolean {
    return this.#disposed;
  }

  setValue(next: T): void {
    if (this.#disposed) return;
    const previous = this.value;
    if (Object.is(previous, next)) return;
    this.#value = next;
    this.onStateChanged(previous, next);
    this.#notify();
  }

  updateValue(update: (old: T) => T): void {
    if (this.#disposed) return;
    this.setValue(update(this.value));
  }

  /** Подписка для `useSyncExternalStore`. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Снимок для `useSyncExternalStore`. */
  getSnapshot = (): T => this.value;

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#listeners.clear();
  }

  #notify(): void {
    // Копия набора: слушатель может отписаться прямо во время обхода.
    for (const listener of [...this.#listeners]) listener();
  }
}
